// The two client-facing controls on a project: park the agent, and take the
// client over personally.
//
// They live in the project page's HEADER, not inside a tab, because an operator
// arriving from an alert needs them without hunting — and because they are the
// only writes on that page, so being visible is also being honest about it.
//
//   - **Pause agent** (POST .../agent-control). The worker keeps persisting the
//     client's messages but stops running turns, so a human answers instead
//     (Conversation tab). Reversible, and on its own it tells the client nothing.
//   - **Stop and notify client** (POST .../takeover) — the manual big-fish
//     handoff. Pops the client "a human from our team will contact you in your
//     Fiverr inbox" and, when the committed budget clears the big-client
//     threshold, enqueues the handoff snapshot that lands as a row in the shared
//     BigQuery big-client table for CS. Notification-only (Monday 3148925054):
//     automation keeps running; pair it with Pause if the agent must go quiet.
//     Idempotent: the repo stamp is the one-shot key, so a double press never
//     re-pops the client or writes twice.
//
// Only Stop is confirm-gated. Pause is reversible and invisible to the client, so
// a dialog on it would train people to click through the one that matters. The
// exception the backend owns: on a project whose committed budget qualifies,
// pausing IS the same client-visible handoff as Stop — which is why the hint says
// so rather than pretending the two are independent.
//
// Support mode never renders this. `web/routes/admin_support.py` is read-only by
// design: speaking as Mira to a client is a policy decision about who may be the
// product's voice, not a UI gap. Do not "fix" that by pointing these at the
// support routes.
import { Badge, Button, ConfirmDialog, useToast } from "@recruiter/shared";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { ApiError, postAgentControl, postTakeover } from "../lib/api";
import "./operator-controls.css";

function detailOf(error: unknown): string | undefined {
  return error instanceof ApiError ? error.detail : undefined;
}

export function OperatorControls({
  projectId,
  agentPaused,
  humanTakeover,
  onChanged,
}: {
  projectId: string;
  agentPaused: boolean;
  humanTakeover: boolean;
  /** Re-read the project. The refetched record is the source of truth for both
   *  states, never our optimistic guess. */
  onChanged: () => void;
}) {
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);

  const pause = useMutation({
    mutationFn: (paused: boolean) => postAgentControl(projectId, paused),
    onSuccess: (result) => {
      toast.add({
        title: result.agentPaused ? "Agent paused" : "Agent resumed",
        description: result.agentPaused
          ? "The client's messages are held for you. Answer in the Conversation tab."
          : undefined,
        data: { tone: "success" },
      });
      onChanged();
    },
    onError: (error: unknown) => {
      toast.add({
        title: "Couldn't change the agent",
        description: detailOf(error),
        data: { tone: "danger" },
      });
    },
  });

  const takeover = useMutation({
    mutationFn: (active: boolean) => postTakeover(projectId, active),
    onSuccess: (result) => {
      setConfirming(false);
      toast.add({
        title: result.humanTakeover ? "Client notified" : "Handoff released",
        description: result.humanTakeover
          ? "The client has been told a specialist will contact them. Automation keeps running."
          : "The contact notice is cleared. If the agent was paused, it is running again.",
        data: { tone: "success" },
      });
      onChanged();
    },
    onError: (error: unknown) => {
      setConfirming(false);
      toast.add({
        title: "Couldn't change the takeover",
        description: detailOf(error),
        data: { tone: "danger" },
      });
    },
  });

  return (
    <div className="oct" role="group" aria-label="Operator controls">
      <div className="oct__state">
        <Badge tone={agentPaused ? "warning" : "success"} size="sm">
          {agentPaused ? "Agent paused" : "Agent active"}
        </Badge>
        {humanTakeover ? (
          <Badge tone="danger" size="sm">
            Human takeover
          </Badge>
        ) : null}
      </div>

      <div className="oct__actions">
        <Button
          variant="outline"
          tone="neutral"
          size="sm"
          disabled={pause.isPending}
          onClick={() => pause.mutate(!agentPaused)}
        >
          {agentPaused ? "Resume agent" : "Pause agent"}
        </Button>
        <Button
          variant="solid"
          tone={humanTakeover ? "neutral" : "danger"}
          size="sm"
          disabled={takeover.isPending}
          onClick={() => setConfirming(true)}
        >
          {humanTakeover ? "Release handoff" : "Stop and notify client"}
        </Button>
      </div>

      <p className="oct__hint">
        {humanTakeover
          ? "The client was told a person will reach them in their Fiverr inbox. Mira and the pipeline keep running meanwhile. Reply as Mira in the Conversation tab, or pause the agent if you need the floor."
          : agentPaused
            ? "Mira is not answering. The client's messages are held for you in the Conversation tab."
            : "Pause holds the client's messages so you can answer. Stop tells them a person will be in touch while automation keeps running. On a big client, pausing does both."}
      </p>

      <ConfirmDialog
        open={confirming}
        title={humanTakeover ? "Release the handoff?" : "Notify this client of a human handoff?"}
        message={
          humanTakeover
            ? "The contact notice clears, and any agent pause that rode along with the handoff is lifted."
            : "The client is told a human from our team will contact them in their Fiverr inbox. Automation keeps running; use Pause if you also need the agent quiet. If their committed budget clears the big-client threshold, CS also gets the handoff snapshot in the shared big-client table."
        }
        confirmLabel={humanTakeover ? "Release" : "Stop and notify"}
        tone={humanTakeover ? "neutral" : "danger"}
        busy={takeover.isPending}
        onConfirm={() => takeover.mutate(!humanTakeover)}
        onCancel={() => setConfirming(false)}
      />
    </div>
  );
}
